import {
  PropsWithChildren,
  Suspense,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { AppStartupLoader } from 'components/common/AppStartupLoader/AppStartupLoader';
import { Loader } from 'components/common/Loader/Loader';
import s from './StartupSuspense.module.css';

const STARTUP_DURATION_MS = 3000;

let isInitialStartupCompleted = false;

type ContentReadyProps = PropsWithChildren<{
  onReady: () => void;
}>;

const ContentReady = ({ onReady, children }: ContentReadyProps) => {
  useEffect(() => {
    onReady();
  }, [onReady]);

  return children;
};

type InitialStartupSuspenseProps = PropsWithChildren<{
  onComplete: () => void;
}>;

const InitialStartupSuspense = ({
  children,
  onComplete,
}: InitialStartupSuspenseProps) => {
  const [isMinTimePassed, setIsMinTimePassed] = useState(false);
  const [isContentReady, setIsContentReady] = useState(false);

  const handleContentReady = useCallback(() => {
    setIsContentReady(true);
  }, []);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setIsMinTimePassed(true);
    }, STARTUP_DURATION_MS);

    return () => window.clearTimeout(timeoutId);
  }, []);

  const isStartupGateDone = isMinTimePassed && isContentReady;

  useEffect(() => {
    if (isStartupGateDone) {
      onComplete();
    }
  }, [isStartupGateDone, onComplete]);

  const shouldShowStartupLoader = !isStartupGateDone;

  return (
    <>
      {shouldShowStartupLoader && <AppStartupLoader />}
      <div
        className={
          shouldShowStartupLoader
            ? s['startup-suspense__content_hidden']
            : undefined
        }
        aria-hidden={shouldShowStartupLoader}
      >
        <Suspense fallback={isStartupGateDone ? <Loader /> : null}>
          <ContentReady onReady={handleContentReady}>{children}</ContentReady>
        </Suspense>
      </div>
    </>
  );
};

export const StartupSuspense = ({ children }: PropsWithChildren) => {
  const [shouldUseStartupGate] = useState(() => !isInitialStartupCompleted);

  const handleStartupComplete = useCallback(() => {
    isInitialStartupCompleted = true;
  }, []);

  if (!shouldUseStartupGate) {
    return <Suspense fallback={<Loader />}>{children}</Suspense>;
  }

  return (
    <InitialStartupSuspense onComplete={handleStartupComplete}>
      {children}
    </InitialStartupSuspense>
  );
};
